import React, { useEffect, useState } from "react";
import { useAuth } from "@/admin/AuthContext";
import {
  Loader2,
  Save,
  Megaphone,
  Power,
  Newspaper,
  Timer,
  RefreshCw,
  ExternalLink,
  ShieldCheck,
  CalendarClock,
  CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const DEFAULT_FORM = {
  enabled: false,
  news_id: "",
  auto_close_seconds: 6,
  show_once_per_session: true,
  title: "",
  subtitle: "",
  starts_at_local: "",
  expires_at_local: "",
};

/* Format ISO/Date -> "yyyy-MM-ddTHH:mm" (datetime-local, local time) */
const toLocalInput = (isoOrDate) => {
  if (!isoOrDate) return "";
  const d = new Date(isoOrDate);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours()
  )}:${pad(d.getMinutes())}`;
};

const fromLocalInput = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const SplashPanel = () => {
  const { authAxios, user } = useAuth();
  const isAdmin = user && (user.role === "admin" || user.role === "superadmin");

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [newsOptions, setNewsOptions] = useState([]);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [splashState, setSplashState] = useState(null); // full server response (for expired info)

  const loadAll = () => {
    setLoading(true);
    Promise.all([
      authAxios.get("/splash"),
      authAxios.get("/news"),
    ])
      .then(([sRes, nRes]) => {
        const s = sRes.data || {};
        // Use stored_enabled (the raw toggle) not the effective enabled — otherwise
        // an expired splash would show the toggle as OFF instead of "expired"
        const rawEnabled =
          s.stored_enabled !== undefined ? !!s.stored_enabled : !!s.enabled;
        setForm({
          enabled: rawEnabled,
          news_id: s.news_id || "",
          auto_close_seconds:
            s.auto_close_seconds !== undefined && s.auto_close_seconds !== null
              ? s.auto_close_seconds
              : 6,
          show_once_per_session:
            s.show_once_per_session === undefined ? true : !!s.show_once_per_session,
          title: s.title || "",
          subtitle: s.subtitle || "",
          starts_at_local: toLocalInput(s.starts_at),
          expires_at_local: toLocalInput(s.expires_at),
        });
        setUpdatedAt(s.updated_at || null);
        setSplashState(s);
        setNewsOptions(nRes.data || []);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || "Gagal memuat data");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (isAdmin) loadAll();
  }, [isAdmin]);

  const save = async (overrides = {}) => {
    setSaving(true);
    const payload = {
      enabled:
        overrides.enabled !== undefined ? overrides.enabled : form.enabled,
      news_id: form.news_id || null,
      auto_close_seconds: Number(form.auto_close_seconds) || 0,
      show_once_per_session: form.show_once_per_session,
      title: form.title || null,
      subtitle: form.subtitle || null,
      starts_at: fromLocalInput(form.starts_at_local),
      expires_at: fromLocalInput(form.expires_at_local),
    };
    try {
      const { data } = await authAxios.put("/admin/splash", payload);
      setUpdatedAt(data.updated_at || new Date().toISOString());
      if (overrides.enabled !== undefined) {
        setForm((f) => ({ ...f, enabled: overrides.enabled }));
      }
      // Refresh full state for accurate expired badge
      try {
        const fresh = await authAxios.get("/splash");
        setSplashState(fresh.data || null);
      } catch {
        /* ignore */
      }
      toast.success(
        payload.enabled
          ? "Splash pengumuman AKTIF"
          : "Splash pengumuman NONAKTIF"
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal menyimpan");
    } finally {
      setSaving(false);
    }
  };

  const clearSessionAndPreview = () => {
    try {
      window.sessionStorage.removeItem("rsb_splash_dismissed");
    } catch {
      /* ignore */
    }
    window.open("/", "_blank", "noopener,noreferrer");
  };

  const selectedNews = newsOptions.find((n) => n.id === form.news_id);

  if (!isAdmin) {
    return (
      <div
        data-testid="admin-splash-forbidden"
        className="p-8 rounded-3xl bg-amber-50 border border-amber-200 text-amber-800"
      >
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck className="h-5 w-5" />
          <div className="font-heading font-semibold">Akses Terbatas</div>
        </div>
        <div className="text-sm">
          Splash Pengumuman hanya dapat diakses oleh admin.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const isActive = form.enabled;
  const isExpired = !!(splashState && splashState.expired);
  const isPending = !!(splashState && splashState.pending);
  const showsToPublic = isActive && !isExpired && !isPending;
  const formatDate = (v) =>
    v
      ? new Date(v).toLocaleString("id-ID", {
          weekday: "long",
          day: "numeric",
          month: "long",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : null;
  const startsDisplay = formatDate(form.starts_at_local);
  const expiresDisplay = formatDate(form.expires_at_local);

  return (
    <div data-testid="admin-panel-splash" className="space-y-6 max-w-3xl">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Megaphone className="h-6 w-6 text-blue-800" />
            Splash & Pengumuman Headline
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Splash screen tampil saat pengunjung pertama membuka website. Dapat
            digunakan untuk menampilkan pengumuman/kegiatan headline dengan tombol
            ke halaman detail berita.
          </p>
        </div>
        <button
          type="button"
          onClick={clearSessionAndPreview}
          data-testid="splash-preview-btn"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-full text-xs font-medium bg-slate-900 text-white hover:bg-slate-800"
        >
          <ExternalLink className="h-3.5 w-3.5" />
          Preview di Tab Baru
        </button>
      </div>

      {/* Status banner */}
      <div
        data-testid="splash-status-banner"
        className={`p-5 rounded-2xl border flex items-start gap-4 ${
          isActive && isExpired
            ? "bg-amber-50 border-amber-200"
            : isActive && isPending
            ? "bg-indigo-50 border-indigo-200"
            : showsToPublic
            ? "bg-emerald-50 border-emerald-200"
            : "bg-slate-50 border-slate-200"
        }`}
      >
        <div
          className={`h-12 w-12 rounded-2xl flex items-center justify-center shrink-0 ${
            isActive && isExpired
              ? "bg-amber-500 text-white"
              : isActive && isPending
              ? "bg-indigo-500 text-white"
              : showsToPublic
              ? "bg-emerald-500 text-white"
              : "bg-slate-400 text-white"
          }`}
        >
          {isActive && isExpired ? (
            <CalendarClock className="h-6 w-6" />
          ) : isActive && isPending ? (
            <CalendarClock className="h-6 w-6" />
          ) : showsToPublic ? (
            <CheckCircle2 className="h-6 w-6" />
          ) : (
            <Megaphone className="h-6 w-6" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div
            className={`font-heading font-bold text-lg ${
              isActive && isExpired
                ? "text-amber-800"
                : isActive && isPending
                ? "text-indigo-800"
                : showsToPublic
                ? "text-emerald-800"
                : "text-slate-700"
            }`}
          >
            {isActive && isExpired
              ? "Splash SUDAH KEDALUWARSA"
              : isActive && isPending
              ? "Splash TERJADWAL"
              : showsToPublic
              ? "Splash Pengumuman AKTIF"
              : "Splash Pengumuman NONAKTIF"}
          </div>
          <div className="text-sm mt-0.5 text-slate-600">
            {isActive && isExpired
              ? "Toggle masih ON tapi waktu berakhir sudah lewat — publik tidak melihat splash."
              : isActive && isPending
              ? "Toggle ON dan waktu mulai belum tiba — publik belum melihat splash. Akan otomatis tampil pada waktu yang ditentukan."
              : showsToPublic
              ? "Pengunjung baru melihat splash dengan pengumuman terpilih."
              : "Pengunjung baru hanya melihat splash loading singkat (logo, ~800ms)."}
          </div>
          {updatedAt && (
            <div className="text-xs text-slate-400 mt-1">
              Terakhir diubah: {new Date(updatedAt).toLocaleString("id-ID")}
            </div>
          )}
        </div>
        <button
          type="button"
          onClick={() => save({ enabled: !isActive })}
          disabled={saving}
          data-testid="splash-quick-toggle"
          className={`shrink-0 inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold shadow-md transition ${
            isActive
              ? "bg-slate-600 hover:bg-slate-700 text-white"
              : "bg-emerald-600 hover:bg-emerald-700 text-white"
          } disabled:opacity-50`}
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Power className="h-4 w-4" />}
          {isActive ? "Nonaktifkan" : "Aktifkan Sekarang"}
        </button>
      </div>

      {/* Form */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 space-y-5">
        {/* News picker */}
        <div>
          <label className="block text-sm font-heading font-semibold text-slate-800 mb-2">
            <Newspaper className="inline h-4 w-4 mr-1 text-blue-700" />
            Berita Headline (link tombol Detail)
          </label>
          <select
            value={form.news_id || ""}
            onChange={(e) => setForm({ ...form, news_id: e.target.value })}
            data-testid="splash-input-news"
            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm bg-white"
          >
            <option value="">— Pilih berita headline —</option>
            {newsOptions.map((n) => (
              <option key={n.id} value={n.id}>
                {n.title_id} ({n.date})
              </option>
            ))}
          </select>
          {selectedNews && (
            <div className="mt-2 flex items-center gap-3 p-2 rounded-xl bg-slate-50 border border-slate-100">
              <img
                src={selectedNews.image}
                alt=""
                className="h-12 w-16 object-cover rounded-lg"
              />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">
                  {selectedNews.title_id}
                </div>
                <div className="text-xs text-slate-500 truncate">
                  {selectedNews.excerpt_id}
                </div>
              </div>
              <a
                href={`/berita/${selectedNews.id}`}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-blue-700 hover:underline shrink-0"
              >
                Buka
              </a>
            </div>
          )}
          <p className="text-xs text-slate-400 mt-1">
            Jika berita dipilih, tombol <b>Baca Selengkapnya</b> akan mengarah ke
            <code className="mx-1 px-1 py-0.5 rounded bg-slate-100 text-blue-700 font-mono">
              /berita/&#123;id&#125;
            </code>
            .
          </p>
        </div>

        {/* Override title */}
        <div>
          <label className="block text-sm font-heading font-semibold text-slate-800 mb-2">
            Judul (opsional — kalau ingin override judul berita)
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            data-testid="splash-input-title"
            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            placeholder="Biarkan kosong untuk pakai judul berita"
          />
        </div>

        {/* Override subtitle */}
        <div>
          <label className="block text-sm font-heading font-semibold text-slate-800 mb-2">
            Subjudul / Ringkasan (opsional)
          </label>
          <textarea
            value={form.subtitle}
            onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
            data-testid="splash-input-subtitle"
            rows={2}
            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm resize-y"
            placeholder="Biarkan kosong untuk pakai excerpt berita"
          />
        </div>

        {/* Schedule: starts_at + expires_at */}
        <div className="p-4 rounded-2xl border border-slate-100 bg-slate-50/60 space-y-4">
          <div className="text-xs font-heading font-semibold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <CalendarClock className="h-3.5 w-3.5" />
            Jadwal Tampil (opsional)
          </div>

          {/* Starts at */}
          <div>
            <label className="block text-sm font-heading font-semibold text-slate-800 mb-2">
              Otomatis Mulai Pada
            </label>
            <div className="flex items-center gap-2">
              <input
                type="datetime-local"
                value={form.starts_at_local}
                onChange={(e) =>
                  setForm({ ...form, starts_at_local: e.target.value })
                }
                data-testid="splash-input-starts"
                className="flex-1 px-4 py-3 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              {form.starts_at_local && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, starts_at_local: "" })}
                  data-testid="splash-clear-starts"
                  className="px-3 py-2 rounded-lg text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700"
                >
                  Hapus
                </button>
              )}
            </div>
            {startsDisplay && (
              <div
                data-testid="splash-starts-display"
                className={`mt-2 text-xs inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${
                  isPending
                    ? "bg-indigo-100 text-indigo-800 border border-indigo-200"
                    : "bg-blue-50 text-blue-800 border border-blue-100"
                }`}
              >
                <CalendarClock className="h-3 w-3" />
                {isPending ? "Terjadwal mulai" : "Mulai pada"}: <b>{startsDisplay}</b>
              </div>
            )}
            <p className="text-xs text-slate-400 mt-1.5">
              Jika diisi, splash <b>belum ditampilkan</b> hingga waktu ini tiba.
              Kosongkan untuk mulai segera setelah diaktifkan.
            </p>
          </div>

          {/* Expires at */}
          <div>
            <label className="block text-sm font-heading font-semibold text-slate-800 mb-2">
              Otomatis Berakhir Pada
            </label>
            <div className="flex items-center gap-2">
              <input
                type="datetime-local"
                value={form.expires_at_local}
                onChange={(e) =>
                  setForm({ ...form, expires_at_local: e.target.value })
                }
                data-testid="splash-input-expires"
                className="flex-1 px-4 py-3 rounded-xl border border-slate-200 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              />
              {form.expires_at_local && (
                <button
                  type="button"
                  onClick={() => setForm({ ...form, expires_at_local: "" })}
                  data-testid="splash-clear-expires"
                  className="px-3 py-2 rounded-lg text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700"
                >
                  Hapus
                </button>
              )}
            </div>
            {expiresDisplay && (
              <div
                data-testid="splash-expires-display"
                className={`mt-2 text-xs inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full ${
                  isExpired
                    ? "bg-amber-100 text-amber-800 border border-amber-200"
                    : "bg-blue-50 text-blue-800 border border-blue-100"
                }`}
              >
                <CalendarClock className="h-3 w-3" />
                {isExpired ? "Sudah lewat" : "Berakhir pada"}: <b>{expiresDisplay}</b>
              </div>
            )}
            <p className="text-xs text-slate-400 mt-1.5">
              Jika diisi, splash otomatis <b>berhenti tampil</b> setelah waktu ini.
              Kosongkan untuk aktif tanpa batas.
            </p>
          </div>
        </div>

        {/* Auto close */}
        <div>
          <label className="block text-sm font-heading font-semibold text-slate-800 mb-2">
            <Timer className="inline h-4 w-4 mr-1 text-blue-700" />
            Otomatis Tertutup (detik)
          </label>
          <input
            type="number"
            min={0}
            max={60}
            value={form.auto_close_seconds}
            onChange={(e) =>
              setForm({ ...form, auto_close_seconds: e.target.value })
            }
            data-testid="splash-input-autoclose"
            className="w-32 px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
          />
          <p className="text-xs text-slate-400 mt-1">
            0 = tidak auto-close (user harus klik Lewati/Detail). Maks 60 detik.
          </p>
        </div>

        {/* Show once per session */}
        <div>
          <label className="inline-flex items-center gap-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={form.show_once_per_session}
              onChange={(e) =>
                setForm({ ...form, show_once_per_session: e.target.checked })
              }
              data-testid="splash-input-once"
              className="h-4 w-4 rounded border-slate-300 text-blue-700 focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-slate-800">
              Tampilkan hanya sekali per sesi browser
            </span>
          </label>
          <p className="text-xs text-slate-400 mt-1 ml-7">
            Direkomendasikan aktif agar tidak mengganggu pengunjung setelah
            dilewati.
          </p>
        </div>

        {/* Save */}
        <div className="flex items-center justify-between gap-2 pt-4 border-t border-slate-100">
          <button
            type="button"
            onClick={() => {
              try {
                window.sessionStorage.removeItem("rsb_splash_dismissed");
                toast.success("Session di-reset. Refresh /home untuk melihat splash.");
              } catch {
                /* ignore */
              }
            }}
            data-testid="splash-reset-session"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-slate-100 hover:bg-slate-200 text-slate-700"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Reset Session (untuk uji lagi)
          </button>
          <button
            type="button"
            onClick={() => save()}
            disabled={saving}
            data-testid="splash-save-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold bg-blue-800 hover:bg-blue-900 text-white shadow-md disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Simpan Konfigurasi
          </button>
        </div>
      </div>

      <div className="p-4 rounded-2xl bg-blue-50 border border-blue-100 text-xs text-blue-900">
        <b>Catatan:</b> Splash disimpan di <code>sessionStorage</code> pengunjung.
        Setelah pengunjung menekan tombol <b>Lewati</b> atau <b>Baca Selengkapnya</b>,
        splash tidak muncul lagi hingga tutup dan buka ulang browser tab.
        Gunakan <code>?admin=1</code> pada URL untuk bypass total (tidak ditampilkan).
      </div>
    </div>
  );
};

export default SplashPanel;
